/* eslint-disable no-use-before-define */
import moment from 'moment-timezone';
import _ from 'underscore';
import { truncate } from 'underscore.string';
import SessionStore from 'stores/SessionStore';
import { getTimezone } from 'helpers/stops';

import { sluggify } from 'utils/localization/utils';
import {
  LOADED_ON_RAIL,
  DEPARTED_ON_RAIL,
  ARRIVED_AT_RAIL_FACILITY,
  UNLOADED_FROM_RAIL,
  AVAILABLE_AT_RAIL_FACILITY,
  FULLY_OUTGATED_FROM_RAIL_FACILITY,
  LAST_FREE_DAY_AT_RAIL_FACILITY,
  ETA_TO_FINAL_RAIL_FACILITY,
  FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  LOADED_AT_RAIL_ORIGIN,
  DEPARTED_FROM_ORIGIN_RAIL,
  ARRIVED_AT_DESTINATION_RAIL_FACILITY,
  UNLOADED_AT_DESTINATION_PORT,
} from '../../helpers/events';

const SHIPMENT_EVENTS = {
  last_free_date: 'Terminal Last Free Date',
  outgated_date: 'Terminal Out Gate',
  empty_returned_date: 'Empty Returned',
  available_date: 'Container Available at Terminal',
  discharge_date: 'Container Discharged',
  arrival_date: 'Vessel Arrived',
  created_at: 'Record Created',
  empty_ready_for_pickup: 'Empty Ready For Pickup',
  ingated_date: 'Terminal In Gate', // TBD
  yard_last_free_date: 'Yard Last Free Date', // TBD
  rail_loaded: LOADED_ON_RAIL,
  rail_departed: DEPARTED_ON_RAIL,
  rail_arrived_at_facility: ARRIVED_AT_RAIL_FACILITY,
  rail_unloaded: UNLOADED_FROM_RAIL,
  rail_available_at_facility: AVAILABLE_AT_RAIL_FACILITY,
  rail_outgated_from_facility: FULLY_OUTGATED_FROM_RAIL_FACILITY,
  rail_lfd_at_facility: LAST_FREE_DAY_AT_RAIL_FACILITY,
  rail_eta_final_facility: ETA_TO_FINAL_RAIL_FACILITY,
  rail_ingated_at_origin_facility: FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  rail_loaded_at_origin: LOADED_AT_RAIL_ORIGIN,
  rail_departed_from_origin: DEPARTED_FROM_ORIGIN_RAIL,
  rail_arrived_at_destination_port_facility: ARRIVED_AT_DESTINATION_RAIL_FACILITY,
  rail_unloaded_at_destination_port: UNLOADED_AT_DESTINATION_PORT,
};

const TIMELINE_WHITELIST = ['en_route', 'needs_appt', 'accepted'];
const TIMELINE_BLACKLIST_FOR_ROLE_EXCEPT_ADMIN = [
  'en_route',
  'needs_appt',
  'accepted',
  'exposed',
  'closed',
  'canceled',
];

const TIMELINE_EVENT_MAPPINGS = {
  // needs_appt: 'Awaiting Appointment Confirmations', //scrapped by product
  en_route: 'En Route',
  accepted: 'Carrier Accepted',
};
const previewableExts = ['pdf', 'jpeg', 'jpg', 'png'];

const eventAttachments = {
  bill_of_lading_uploaded: 'bill_of_lading',
};

function formatTime(momentDate) {
  return momentDate.format('LL, LT');
}

const formatLocation = (stop, isLoggedIn = false) => {
  if (isLoggedIn) {
    const address =
      stop && stop?.location[0]?.address ? stop.location[0].address : null;
    return address ? truncate(address, 50) : null;
  }
  const address =
    stop && stop?.location[0]?.city && stop?.location[0]?.state
      ? `${stop.location[0].city}, ${stop.location[0].state}`
      : null;
  return address ? truncate(address, 50) : null;
};

const formatEvent = (stop, slug) => {
  const empty = stop.empty ? 'Empty_' : '';
  const offsite = stop.off_site ? 'Offsite_' : '';
  let event;
  if (slug === 'complete') {
    event = `${offsite}${empty}${stop.action}_${slug}`;
  } else {
    event = `${slug}_at_${offsite}${empty}${stop.action}`;
  }
  return truncate(event, 50);
};

const getStopTimeline = (stops) => {
  const isLoggedIn = SessionStore.isLoggedIn();
  return stops.reduce((events, stop) => {
    const items = _.filter(stop.timeline, (timeline) => {
      return timeline.status.slug !== 'unstarted';
    });

    const timelineItems = items.map((item) => {
      const time = item.meta && item.meta.date ? item.meta.date : item.at; // Reported vs Occurred

      let reason;
      if (item.meta && item.status.slug.startsWith('attempt')) {
        reason = item.meta?.reason?.description;
      }

      const timezone = getTimezone(stop);
      const momentDate = moment(time).tz(timezone);
      const location = formatLocation(stop, isLoggedIn);
      return {
        stopId: stop.id,
        reason,
        event: formatEvent(stop, item.status.slug),
        at: momentDate,
        formattedTime: formatTime(momentDate),
        location,
        signatoryField: null,
        status: item.status,
      };
    });

    return events.concat(timelineItems);
  }, []);
};

const getShipmentAttachment = (shipment, key) => {
  // We've seen some older shipment docs without a 'show' attribute
  const attachment = shipment.documents
    ? shipment.documents.find((doc) => {
        return key === doc.key && doc.show;
      })
    : null;
  if (attachment) {
    const ext = (attachment.uri.split('.').pop() || '').toLowerCase();
    const isPreviewable = previewableExts.includes(ext);
    const prettyFilename = `cargomatic-${shipment.shipment_id}-${key}.${ext}`;
    return {
      file: attachment.uri,
      isPreviewable,
      prettyFilename,
    };
  }
};

const formatReportedHold = (hold) => {
  return `${hold.type} hold reported`; // TBD
};

const formatResolvedHold = (hold) => {
  return `resolved ${hold.type} hold`; // TBD
};

const getHoldList = (shipment) => {
  const { holds } = shipment;
  if (!holds || !holds.length) return [];

  const origin = shipment.origin[0];
  const timezone = origin.location_timezone || origin.market.timezone;

  const reportedEvents = holds.filter((hold) => hold.reported_at);
  const reportedHolds = reportedEvents.map((hold) => {
    const momentDate = moment(hold.reported_at).tz(timezone);
    return {
      reason: hold.remarks ? truncate(hold.remarks, 30) : null,
      event: formatReportedHold(hold),
      at: momentDate,
      formattedTime: formatTime(momentDate),
      location: null,
      signatoryField: null,
      status: null,
      attachment: null,
    };
  });

  const resolvedEvents = holds.filter((hold) => hold.resolved_at);
  const resolvedHolds = resolvedEvents.map((hold) => {
    const momentDate = moment(hold.resolved_at).tz(timezone);
    return {
      reason: hold.remarks ? truncate(hold.remarks, 30) : null,
      event: formatResolvedHold(hold),
      at: momentDate,
      formattedTime: formatTime(momentDate),
      location: null,
      signatoryField: null,
      status: null,
      attachment: null,
    };
  });

  return [...reportedHolds, ...resolvedHolds];
};

const getShipmentEvents = (shipment) => {
  const timelineList = getShipmentTimeline(shipment);
  const holdList = getHoldList(shipment);
  const shipmentEventList = getNonTimelineEvents(shipment);
  return [...timelineList, ...holdList, ...shipmentEventList];
};

const filterShipmentEvents = (shipment) => {
  let events = { ...SHIPMENT_EVENTS };
  if (shipment.shipment_type === 'rail' && shipment.rail_arrived_at_facility) {
    events.arrival_date = 'Train Arrived';
  }
  return events;
};

const getNonTimelineEvents = (shipment) => {
  const EVENTS = filterShipmentEvents(shipment);
  const availableEvents = _.filter(Object.keys(EVENTS), (event) => {
    return shipment[event];
  });
  const origin = shipment.origin[0];
  const timezone = origin.location_timezone || origin.market.timezone;

  const mappedEvents = availableEvents.map((event) => {
    const eventName = EVENTS[event];

    const date = new Date(shipment[event]);
    const momentDate = moment(date).tz(timezone);
    return {
      event: eventName,
      reason: null,
      at: momentDate,
      formattedTime: formatTime(momentDate),
      attachment: null,
      signatoryField: null,
    };
  });

  if (!shipment.arrival_date && shipment.estimated_arrival) {
    mappedEvents.push({
      event: 'Estimated Time of Arrival',
      reason: null,
      at: moment(shipment.estimated_arrival).tz(timezone),
      formattedTime: formatTime(
        moment(shipment.estimated_arrival).tz(timezone),
      ),
      attachment: null,
      signatoryField: null,
    });
  }
  return mappedEvents;
};

const normalizeTimelineEvent = (event) => {
  return TIMELINE_EVENT_MAPPINGS[event] || event;
};

const getShipmentTimeline = (shipment) => {
  const { timeline, signatory, signatory_pickup } = shipment;
  const validTimelineEvents = _.pick(timeline, TIMELINE_WHITELIST);

  return Object.keys(validTimelineEvents).map((event) => {
    let attachment = null;
    let signatoryField = null;
    const documentKey = eventAttachments[event];

    if (documentKey) {
      attachment = getShipmentAttachment(shipment, documentKey);
      signatoryField = documentKey.includes('pickup')
        ? signatory_pickup
        : signatory;
    }

    let reason;
    const item = timeline[event];
    if (item.meta && event.startsWith('attempt')) {
      reason = item?.meta?.reason?.description;
    }

    const origin = shipment.origin[0];
    const timezone = origin.location_timezone || origin.market.timezone;

    const timestamp = getTimelineDate(item, event);
    const date = moment(timestamp).tz(timezone);

    const transFormedEventName = normalizeTimelineEvent(event);

    return {
      reason,
      event: transFormedEventName,
      ...item,
      at: date,
      formattedTime: formatTime(date),
      attachment,
      signatoryField,
    };
  });
};

const getTimelineDate = (item, event) => {
  switch (event) {
    case 'en_route':
      return item?.meta?.timestamp || item?.meta?.date || item.at;
    case 'accepted':
      return item?.meta?.timestamp || item?.meta?.date || item.at;
    default:
      return item.at;
  }
};

export function mapTimeline(shipment) {
  const shipmentEventList = getShipmentEvents(shipment);
  const stopTimelineList = getStopTimeline(shipment.stops);
  const events = [...stopTimelineList, ...shipmentEventList];
  const isNotAdmin = !SessionStore.isAdmin();

  let finalEvents = events;

  if (isNotAdmin) {
    finalEvents = events.filter(
      (ev) =>
        ev?.event &&
        !TIMELINE_BLACKLIST_FOR_ROLE_EXCEPT_ADMIN.includes(
          sluggify(ev.event).toLowerCase(),
        ),
    );
  }

  return [...finalEvents].sort((a, b) => b.at - a.at);
}
